package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import se.mittpass.shared.api.AbsenceRequestDto
import se.mittpass.shared.api.StaffMemberDto
import se.mittpass.shared.api.UpdateAbsenceRequest
import se.mittpass.shared.api.CreateAbsenceRequest
import se.mittpass.shared.api.WorkplaceRequirementDto
import se.mittpass.shared.api.CustomRoleDto
import se.mittpass.shared.api.CreateWorkplaceRequirementRequest
import se.mittpass.shared.api.RequirementDayCountDto

private val semesterScope = MainScope()

@Composable
fun SemesterPage(workplaceId: Long, isManager: Boolean, currentUserId: Long?) {
    var absences by remember { mutableStateOf<List<AbsenceRequestDto>>(emptyList()) }
    var staff by remember { mutableStateOf<List<StaffMemberDto>>(emptyList()) }
    var requirements by remember { mutableStateOf<List<WorkplaceRequirementDto>>(emptyList()) }
    var allRoles by remember { mutableStateOf<List<CustomRoleDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }

    // Month Navigation State
    val today = kotlin.js.Date()
    var currentYear by remember { mutableStateOf(today.getFullYear()) }
    var currentMonth by remember { mutableStateOf(today.getMonth() + 1) } // 1-indexed

    // Calendar selection state (for drag/range creation): Pair<UserId, Day>
    var activeSelection by remember { mutableStateOf<Pair<Long, Int>?>(null) }

    // Modal State
    var editingAbsence by remember { mutableStateOf<AbsenceRequestDto?>(null) }
    var showCreateModal by remember { mutableStateOf(false) }
    var showRequirementModal by remember { mutableStateOf(false) }
    var editingRequirementId by remember { mutableStateOf<Long?>(null) }

    // Form inputs for creation modal
    var createSelectedUser by remember { mutableStateOf<Long?>(null) }
    var createStartInput by remember { mutableStateOf("") }
    var createEndInput by remember { mutableStateOf("") }
    var createNoteInput by remember { mutableStateOf("") }
    var createStatusInput by remember { mutableStateOf("approved") }

    fun loadData() {
        loading = true
        semesterScope.launch {
            absenceRepo.getAbsences(workplaceId).onSuccess { absences = it }
            staffRepo.getStaff(workplaceId).onSuccess { staff = it.filter { member -> member.active } }
            requirementRepo.getRequirements(workplaceId).onSuccess { requirements = it }
            customRoleRepo.getRoles(workplaceId).onSuccess { allRoles = it }
            loading = false
        }
    }

    LaunchedEffect(workplaceId) {
        loadData()
    }

    var windowWidth by remember { mutableStateOf(kotlinx.browser.window.innerWidth) }
    val isMobile = windowWidth < 768

    DisposableEffect(Unit) {
        val handler: (org.w3c.dom.events.Event) -> Unit = { windowWidth = kotlinx.browser.window.innerWidth }
        kotlinx.browser.window.addEventListener("resize", handler)
        onDispose { kotlinx.browser.window.removeEventListener("resize", handler) }
    }

    // Helper for month days
    val daysInMonth = remember(currentYear, currentMonth) {
        kotlin.js.Date(currentYear, currentMonth, 0).getDate()
    }

    val monthName = remember(currentMonth) {
        listOf(
            "Januari", "Februari", "Mars", "April", "Maj", "Juni",
            "Juli", "Augusti", "September", "Oktober", "November", "December"
        )[currentMonth - 1]
    }

    fun nextMonth() {
        if (currentMonth == 12) {
            currentMonth = 1
            currentYear++
        } else {
            currentMonth++
        }
        activeSelection = null
    }

    fun prevMonth() {
        if (currentMonth == 1) {
            currentMonth = 12
            currentYear--
        } else {
            currentMonth--
        }
        activeSelection = null
    }

    fun getRequirementCount(day: Int, roleId: Long): Int {
        val dateStr = "$currentYear-${currentMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
        val req = requirements.firstOrNull { r ->
            dateStr >= r.startDate && dateStr <= r.endDate
        } ?: return 0
        
        val dateObj = kotlin.js.Date(currentYear, currentMonth - 1, day)
        val jsDay = dateObj.getDay()
        val dayOfWeek = if (jsDay == 0) 7 else jsDay
        
        return req.days.firstOrNull { it.roleId == roleId && it.dayOfWeek == dayOfWeek }?.count ?: 0
    }

    fun getAvailableStaffCount(day: Int, roleId: Long): Int {
        val dateStr = "$currentYear-${currentMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
        return staff.count { member ->
            val hasRole = member.customRoles.any { it.id == roleId }
            if (!hasRole) return@count false
            
            val onVacation = absences.any { vac ->
                vac.userId == member.userId && vac.type == "vacation" && vac.status == "approved" &&
                vac.startDate <= dateStr && vac.endDate >= dateStr
            }
            !onVacation
        }
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", if (msg.contains("raderad") || msg.contains("skapad") || msg.contains("uppdaterad")) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("color", if (msg.contains("raderad") || msg.contains("skapad") || msg.contains("uppdaterad")) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                }
            }) { Text(msg) }
        }

        // ── Toolbar/Header ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "1.1rem 1.4rem")
                property("border", "1px solid var(--rp-hl-med)")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("gap", "1rem")
                property("flex-wrap", "wrap")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
            }
        }) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "1.2rem")
                    property("flex-wrap", "wrap")
                }
            }) {
                H3({
                    style {
                        property("margin", "0")
                        property("font-size", "1.25rem")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                        property("letter-spacing", "-0.3px")
                    }
                }) {
                    Text("Semesterplanering")
                }
                
                // Navigation Pill (Month & Year)
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("background", "var(--rp-overlay)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("padding", "0.2rem 0.35rem")
                        property("border-radius", "50px")
                        property("gap", "0.25rem")
                    }
                }) {
                    Button({
                        onClick { prevMonth() }
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            property("cursor", "pointer")
                            property("width", "28px")
                            property("height", "28px")
                            property("border-radius", "50%")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("color", "var(--rp-subtle)")
                            property("transition", "background 0.15s ease")
                        }
                    }) {
                        FaIcon("fa-solid fa-chevron-left", size = "0.75rem", color = "var(--rp-subtle)")
                    }
                    
                    Span({
                        style {
                            property("font-size", "0.84rem")
                            property("font-weight", "700")
                            property("color", "var(--rp-text)")
                            property("min-width", "130px")
                            property("text-align", "center")
                            property("padding", "0 0.4rem")
                        }
                    }) {
                        Text("$monthName $currentYear")
                    }

                    Button({
                        onClick { nextMonth() }
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            property("cursor", "pointer")
                            property("width", "28px")
                            property("height", "28px")
                            property("border-radius", "50%")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("color", "var(--rp-subtle)")
                            property("transition", "background 0.15s ease")
                        }
                    }) {
                        FaIcon("fa-solid fa-chevron-right", size = "0.75rem", color = "var(--rp-subtle)")
                    }
                }
            }

            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.6rem")
                    property("flex-wrap", "wrap")
                }
            }) {
                if (isManager) {
                    Button({
                        onClick { showRequirementModal = true }
                        style {
                            property("padding", "0.55rem 1.1rem")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-subtle)")
                            property("border-radius", "50px")
                            property("font-weight", "700")
                            property("font-size", "0.82rem")
                            property("cursor", "pointer")
                            property("font-family", "inherit")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.45rem")
                            property("box-shadow", "0 1px 3px rgba(0,0,0,0.02)")
                            property("transition", "all 0.15s ease")
                        }
                    }) {
                        FaIcon("fa-solid fa-sliders", size = "0.8rem", color = "var(--rp-subtle)")
                        Text("Planeringskrav")
                    }
                }
                Button({
                    onClick {
                        createSelectedUser = if (isManager) staff.firstOrNull()?.userId else currentUserId
                        createStartInput = todayLocalDate()
                        createEndInput = todayLocalDate()
                        createNoteInput = ""
                        createStatusInput = if (isManager) "approved" else "pending"
                        showCreateModal = true
                    }
                    style {
                        property("padding", "0.55rem 1.25rem")
                        property("border", "none")
                        property("background", "var(--rp-pine)")
                        property("color", "#FFFFFF")
                        property("border-radius", "50px")
                        property("font-weight", "700")
                        property("font-size", "0.84rem")
                        property("cursor", "pointer")
                        property("font-family", "inherit")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.45rem")
                        property("box-shadow", "0 2px 8px rgba(79,70,229,0.25)")
                        property("transition", "all 0.15s ease")
                    }
                }) {
                    FaIcon("fa-solid fa-plus", size = "0.8rem", color = "var(--rp-surface)")
                    Text("Ansök om semester")
                }
            }
        }

        // Hint bar (Modern subtle banner with left accent and icon)
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-left", "4px solid var(--rp-pine)")
                property("border-radius", "14px")
                property("padding", "0.75rem 1.1rem")
                property("font-size", "0.82rem")
                property("font-weight", "600")
                property("color", "var(--rp-text)")
                property("display", "flex")
                property("align-items", "center")
                property("gap", "0.65rem")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.02)")
            }
        }) {
            FaIcon("fa-solid fa-circle-info", size = "0.95rem", color = "var(--rp-pine)")
            Text(
                if (isManager) "Tips: Klicka på startdatum och sedan slutdatum i kalendern för att snabbt lägga in en semesterperiod."
                else "Tips: Klicka på ditt startdatum och sedan slutdatum på din rad för att snabbt ansöka om semester."
            )
        }

        if (isMobile) {
            MobileSemesterView(
                currentYear = currentYear,
                currentMonth = currentMonth,
                staff = staff,
                absences = absences,
                isManager = isManager,
                currentUserId = currentUserId,
                onAbsenceClick = { abs -> editingAbsence = abs },
                onAddVacation = {
                    createSelectedUser = if (isManager) staff.firstOrNull()?.userId else currentUserId
                    createStartInput = todayLocalDate()
                    createEndInput = todayLocalDate()
                    createNoteInput = ""
                    createStatusInput = if (isManager) "approved" else "pending"
                    showCreateModal = true
                }
            )
        } else {
            // ── Gantt Chart Container (Desktop) ──
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem")
                    property("box-shadow", "0 1px 3px rgba(13,13,24,0.04)"); property("overflow-x", "auto")
                    property("display", "flex"); property("flex-direction", "column"); property("min-width", "0")
                }
            }) {
            if (loading) {
                Div({ style { property("text-align", "center"); property("padding", "4rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar...") }
            } else if (staff.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "4rem"); property("color", "var(--rp-muted)") } }) { Text("Inga medarbetare hittades.") }
            } else {
                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "240px repeat($daysInMonth, 40px)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("border-radius", "16px")
                        property("overflow", "hidden")
                        property("width", "max-content")
                        property("min-width", "100%")
                    }
                }) {
                    // Left Corner Header Cell (sticky)
                    Div({
                        style {
                            property("background", "var(--rp-overlay)")
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                            property("border-right", "1px solid var(--rp-hl-med)")
                            property("height", "50px")
                            property("display", "flex")
                            property("align-items", "center")
                            property("padding-left", "1rem")
                            property("font-weight", "700")
                            property("color", "var(--rp-subtle)")
                            property("font-size", "0.75rem")
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px")
                            property("position", "sticky")
                            property("left", "0")
                            property("z-index", "10")
                        }
                    }) {
                        Text("Medarbetare")
                    }

                    // Right Timeline Header (Days)
                    for (d in 1..daysInMonth) {
                        val dateObj = kotlin.js.Date(currentYear, currentMonth - 1, d)
                        val dayOfWeek = dateObj.getDay() // 0 = Sun, 6 = Sat
                        val isWeekend = dayOfWeek == 0 || dayOfWeek == 6
                        Div({
                            style {
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("border-right", "1px solid var(--rp-hl-med)")
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                                property("font-size", "0.7rem")
                                property("font-weight", "700")
                                property("color", if (isWeekend) "var(--rp-err-text)" else "var(--rp-subtle)")
                                if (isWeekend) property("background", "var(--rp-err-bg)")
                                else property("background", "var(--rp-overlay)")
                                property("grid-row", "1")
                                property("grid-column", "${d + 1}")
                                property("height", "50px")
                            }
                        }) {
                            Span { Text(listOf("Sö", "Må", "Ti", "On", "To", "Fr", "Lö")[dayOfWeek]) }
                            Span({ style { property("font-size", "0.8rem"); property("margin-top", "2px") } }) { Text("$d") }
                        }
                    }

                    // Grid rows
                    staff.forEachIndexed { i, member ->
                        val memberName = listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { member.email }
                        val rowNum = i + 2 // Row 1 is header
                        val isSelf = member.userId == currentUserId

                        // Left Staff Cell (sticky)
                        Div({
                            style {
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                                property("border-right", "1px solid var(--rp-hl-med)")
                                property("height", "70px")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.75rem")
                                property("padding", "0 1rem")
                                property("background", "var(--rp-surface)")
                                property("position", "sticky")
                                property("left", "0")
                                property("z-index", "9")
                                property("grid-row", "$rowNum")
                                property("grid-column", "1")
                            }
                        }) {
                            // Avatar
                            Div({
                                style {
                                    property("width", "36px"); property("height", "36px")
                                    property("border-radius", "50%"); property("background", "var(--rp-info-bg)")
                                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                                    property("color", "var(--rp-pine)"); property("font-weight", "700"); property("font-size", "0.85rem")
                                }
                            }) {
                                Text(memberName.split(" ").mapNotNull { it.firstOrNull() }.joinToString("").take(2).uppercase())
                            }
                            Div {
                                Div({ style { property("font-weight", "600"); property("font-size", "0.85rem"); property("color", "var(--rp-text)") } }) {
                                    Text(memberName + if (isSelf) " (Du)" else "")
                                }
                                Div({ style { property("font-size", "0.7rem"); property("color", "var(--rp-muted)") } }) {
                                    Text(member.email)
                                }
                            }
                        }

                        // Right Grid Day Cells Background
                        for (d in 1..daysInMonth) {
                            val dateObj = kotlin.js.Date(currentYear, currentMonth - 1, d)
                            val dayOfWeek = dateObj.getDay()
                            val isWeekend = dayOfWeek == 0 || dayOfWeek == 6

                            // Highlight selected start cell
                            val isSelectedStart = activeSelection?.first == member.userId && activeSelection?.second == d

                            Div({
                                onClick {
                                    // Staff can only click cells on their own row
                                    if (!isManager && member.userId != currentUserId) return@onClick

                                    val currentSel = activeSelection
                                    if (currentSel == null || currentSel.first != member.userId) {
                                        activeSelection = member.userId to d
                                    } else {
                                        val firstDay = currentSel.second
                                        val startDay = minOf(firstDay, d)
                                        val endDay = maxOf(firstDay, d)

                                        createSelectedUser = member.userId
                                        createStartInput = "$currentYear-${currentMonth.toString().padStart(2, '0')}-${startDay.toString().padStart(2, '0')}"
                                        createEndInput = "$currentYear-${currentMonth.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')}"
                                        createNoteInput = ""
                                        createStatusInput = if (isManager) "approved" else "pending"
                                        showCreateModal = true
                                        activeSelection = null
                                    }
                                }
                                style {
                                    property("border-right", "1px solid var(--rp-hl-med)")
                                    property("border-bottom", "1px solid var(--rp-hl-med)")
                                    property("height", "70px")
                                    if (isManager || isSelf) {
                                        property("cursor", "pointer")
                                    }
                                    if (isSelectedStart) {
                                        property("background", "var(--rp-info-border)") // Blue highlight for selected start
                                    } else if (isWeekend) {
                                        property("background", "var(--rp-err-bg)")
                                    } else {
                                        property("background", "var(--rp-surface)")
                                    }
                                    property("grid-row", "$rowNum")
                                    property("grid-column", "${d + 1}")
                                }
                            }) {}
                        }

                        // Render vacation bars on top
                        val memberVacations = absences.filter { it.userId == member.userId && it.type == "vacation" }
                        memberVacations.forEach { vac ->
                            val startD = kotlin.js.Date("${vac.startDate}T12:00:00")
                            val endD = kotlin.js.Date("${vac.endDate}T12:00:00")

                            val startYear = startD.getFullYear()
                            val startMonth = startD.getMonth() + 1
                            val endYear = endD.getFullYear()
                            val endMonth = endD.getMonth() + 1

                            // Check if vacation spans or overlaps current view month
                            val currentMonthFirst = kotlin.js.Date(currentYear, currentMonth - 1, 1)
                            val currentMonthLast = kotlin.js.Date(currentYear, currentMonth, 0)

                            if (!(endD.getTime() < currentMonthFirst.getTime() || startD.getTime() > currentMonthLast.getTime())) {
                                // Calculate grid position
                                val startDay = if (startYear == currentYear && startMonth == currentMonth) startD.getDate() else 1
                                val endDay = if (endYear == currentYear && endMonth == currentMonth) endD.getDate() else daysInMonth

                                val (barBg, barColor, barBorder) = when (vac.status) {
                                    "approved" -> Triple("var(--rp-ok-bg)", "var(--rp-ok-text)", "var(--rp-ok-border)")
                                    "rejected" -> Triple("var(--rp-err-bg)", "var(--rp-err-text)", "var(--rp-err-border)")
                                    else -> Triple("var(--rp-warn-bg)", "var(--rp-warn-text)", "var(--rp-warn-border)")
                                }

                                Div({
                                    onClick { editingAbsence = vac }
                                    style {
                                        property("grid-row", "$rowNum")
                                        property("grid-column", "${startDay + 1} / ${endDay + 2}")
                                        property("background", barBg)
                                        property("color", barColor)
                                        property("border", "1px solid $barBorder")
                                        property("margin", "auto 2px")
                                        property("border-radius", "8px")
                                        property("height", "40px")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("padding", "0 0.5rem")
                                        property("font-size", "0.75rem")
                                        property("font-weight", "600")
                                        property("cursor", "pointer")
                                        property("z-index", "5")
                                        property("box-shadow", "0 4px 6px -1px rgba(0,0,0,0.05)")
                                    }
                                }) {
                                    FaIcon("fa-solid fa-umbrella-beach", size = "0.7rem")
                                    Span({ style { property("margin-left", "0.3rem"); property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap") } }) {
                                        Text(if (vac.note.isNullOrBlank()) "Semester" else vac.note!!)
                                    }
                                }
                            }
                        }
                    }

                    // ── RETAIL & STAFF REQUIREMENTS SECTION ──
                    if (allRoles.isNotEmpty()) {
                        val separatorRow = staff.size + 2
                        // Separator row
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border-bottom", "1px solid var(--rp-hl-med)")
                                property("border-right", "1px solid var(--rp-hl-med)")
                                property("height", "32px")
                                property("display", "flex")
                                property("align-items", "center")
                                property("padding-left", "1rem")
                                property("font-weight", "800")
                                property("color", "var(--rp-subtle)")
                                property("font-size", "0.7rem")
                                property("text-transform", "uppercase")
                                property("letter-spacing", "0.5px")
                                property("position", "sticky")
                                property("left", "0")
                                property("z-index", "9")
                                property("grid-row", "$separatorRow")
                                property("grid-column", "1")
                            }
                        }) {
                            Text("Krav & Beläggning")
                        }
                        for (d in 1..daysInMonth) {
                            val dateObj = kotlin.js.Date(currentYear, currentMonth - 1, d)
                            val dayOfWeek = dateObj.getDay()
                            val isWeekend = dayOfWeek == 0 || dayOfWeek == 6
                            Div({
                                style {
                                    property("background", "var(--rp-overlay)")
                                    property("border-right", "1px solid var(--rp-hl-med)")
                                    property("border-bottom", "1px solid var(--rp-hl-med)")
                                    property("height", "32px")
                                    property("grid-row", "$separatorRow")
                                    property("grid-column", "${d + 1}")
                                }
                            }) {}
                        }

                        // One row per custom role
                        allRoles.forEachIndexed { roleIdx, role ->
                            val rowNum = separatorRow + 1 + roleIdx

                            // Left Cell
                            Div({
                                style {
                                    property("border-bottom", "1px solid var(--rp-hl-med)")
                                    property("border-right", "1px solid var(--rp-hl-med)")
                                    property("height", "45px")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("padding", "0 1rem")
                                    property("background", "var(--rp-overlay)")
                                    property("position", "sticky")
                                    property("left", "0")
                                    property("z-index", "9")
                                    property("grid-row", "$rowNum")
                                    property("grid-column", "1")
                                }
                            }) {
                                Span({ style { property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)") } }) {
                                    Text(role.name)
                                }
                            }

                            // Day cells
                            for (d in 1..daysInMonth) {
                                val req = getRequirementCount(d, role.id)
                                val avail = getAvailableStaffCount(d, role.id)
                                val isOver = avail >= req

                                Div({
                                    style {
                                        property("border-right", "1px solid var(--rp-hl-med)")
                                        property("border-bottom", "1px solid var(--rp-hl-med)")
                                        property("height", "45px")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("grid-row", "$rowNum")
                                        property("grid-column", "${d + 1}")
                                        
                                        if (req > 0) {
                                            if (isOver) {
                                                property("background", "var(--rp-ok-bg)")
                                            } else {
                                                property("background", "var(--rp-err-bg)")
                                            }
                                        } else {
                                            property("background", "var(--rp-surface)")
                                        }
                                    }
                                }) {
                                    if (req > 0) {
                                        Span({
                                            style {
                                                property("font-size", "0.78rem"); property("font-weight", "800")
                                                if (isOver) {
                                                    property("color", "var(--rp-ok-text)")
                                                } else {
                                                    property("color", "var(--rp-err-text)")
                                                }
                                            }
                                        }) {
                                            Text("$avail/$req")
                                        }
                                    } else {
                                        Span({ style { property("color", "var(--rp-hl-med)"); property("font-size", "0.78rem") } }) {
                                            Text("-")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // CREATE ABSENCE REQUEST MODAL
    if (showCreateModal) {
        var isSaving by remember { mutableStateOf(false) }

        Div({
            onClick { showCreateModal = false }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(13,13,24,0.45)"); property("backdrop-filter", "blur(4px)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem 2.25rem")
                    property("width", "100%"); property("max-width", "480px")
                    property("box-shadow", "0 24px 80px rgba(13,13,24,0.18)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.5rem 0"); property("font-size", "1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Ny semesteransökan")
                }

                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                    if (isManager) {
                        Div {
                            Span({ style { absenceLabelStyle() } }) { Text("Medarbetare") }
                            Select({
                                onInput { createSelectedUser = it.value?.toLongOrNull() }
                                style { absenceInputStyle() }
                            }) {
                                staff.forEach { m ->
                                    val n = listOfNotNull(m.firstName, m.lastName).joinToString(" ").ifEmpty { m.email }
                                    Option(m.userId.toString(), attrs = { if (createSelectedUser == m.userId) selected() }) { Text(n) }
                                }
                            }
                        }
                    } else {
                        Div {
                            Span({ style { absenceLabelStyle() } }) { Text("Medarbetare") }
                            val selfName = staff.find { it.userId == currentUserId }?.let { listOfNotNull(it.firstName, it.lastName).joinToString(" ") } ?: "Du"
                            Div({
                                style {
                                    property("padding", "0.7rem 1rem"); property("background", "var(--rp-overlay)")
                                    property("border-radius", "14px"); property("font-weight", "600"); property("font-size", "1rem")
                                    property("color", "var(--rp-text)")
                                }
                            }) {
                                Text(selfName)
                            }
                        }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Startdatum") }
                        Input(InputType.Date) {
                            value(createStartInput)
                            onInput { createStartInput = it.value }
                            style { absenceInputStyle() }
                        }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Slutdatum") }
                        Input(InputType.Date) {
                            value(createEndInput)
                            onInput { createEndInput = it.value }
                            style { absenceInputStyle() }
                        }
                    }

                    if (isManager) {
                        Div {
                            Span({ style { absenceLabelStyle() } }) { Text("Status") }
                            Select({
                                onInput { createStatusInput = it.value ?: "approved" }
                                style { absenceInputStyle() }
                            }) {
                                Option("approved", attrs = { if (createStatusInput == "approved") selected() }) { Text("Godkänd (Standard)") }
                                Option("pending", attrs = { if (createStatusInput == "pending") selected() }) { Text("Väntar") }
                                Option("rejected", attrs = { if (createStatusInput == "rejected") selected() }) { Text("Nekad") }
                            }
                        }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Anteckning") }
                        TextArea {
                            value(createNoteInput)
                            onInput { createNoteInput = it.value }
                            style {
                                absenceInputStyle()
                                property("height", "80px")
                            }
                        }
                    }

                    Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "0.5rem"); property("margin-top", "1rem") } }) {
                        Button({
                            onClick { showCreateModal = false }
                            style { property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)"); property("border", "none"); property("padding", "0.6rem 1rem"); property("border-radius", "8px"); property("font-weight", "600"); property("cursor", "pointer") }
                        }) { Text("Avbryt") }

                        Button({
                            onClick {
                                val uId = if (isManager) createSelectedUser else currentUserId
                                if (uId == null || isSaving) return@onClick
                                isSaving = true
                                semesterScope.launch {
                                    absenceRepo.createAbsence(workplaceId, CreateAbsenceRequest(
                                        type = "vacation",
                                        startDate = createStartInput,
                                        endDate = createEndInput,
                                        note = createNoteInput.ifBlank { null },
                                        userId = uId,
                                        status = if (isManager) createStatusInput else "pending"
                                    )).onSuccess {
                                        feedbackMsg = "Semesteransökan skapad"
                                        showCreateModal = false
                                        loadData()
                                    }.onFailure {
                                        feedbackMsg = "Kunde inte skapa semesteransökan"
                                    }
                                    isSaving = false
                                }
                            }
                            style { property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white"); property("border", "none"); property("padding", "0.6rem 1.2rem"); property("border-radius", "8px"); property("font-weight", "600"); property("cursor", "pointer") }
                        }) { Text(if (isManager) "Skapa" else "Skicka ansökan") }
                    }
                }
            }
        }
    }

    // EDIT ABSENCE REQUEST MODAL
    editingAbsence?.let { vac ->
        var isSaving by remember { mutableStateOf(false) }
        var isDeleting by remember { mutableStateOf(false) }

        var startInput by remember { mutableStateOf(vac.startDate) }
        var endInput by remember { mutableStateOf(vac.endDate) }
        var noteInput by remember { mutableStateOf(vac.note ?: "") }
        var statusInput by remember { mutableStateOf(vac.status) }

        Div({
            onClick { editingAbsence = null }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(13,13,24,0.45)"); property("backdrop-filter", "blur(4px)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem 2.25rem")
                    property("width", "100%"); property("max-width", "480px")
                    property("box-shadow", "0 24px 80px rgba(13,13,24,0.18)")
                }
            }) {
                H3({ style { property("margin", "0 0 1.5rem 0"); property("font-size", "1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text(if (isManager) "Hantera semesteransökan" else "Visa semesteransökan")
                }

                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Medarbetare") }
                        val n = staff.find { it.userId == vac.userId }?.let { listOfNotNull(it.firstName, it.lastName).joinToString(" ") } ?: vac.userEmail
                        Div({
                            style {
                                property("padding", "0.7rem 1rem"); property("background", "var(--rp-overlay)")
                                property("border-radius", "14px"); property("font-weight", "600"); property("font-size", "1rem")
                                property("color", "var(--rp-text)")
                            }
                        }) { Text(n) }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Startdatum") }
                        Input(InputType.Date) {
                            value(startInput)
                            onInput { startInput = it.value }
                            if (!isManager && vac.userId != currentUserId) attr("readonly", "true")
                            style { absenceInputStyle() }
                        }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Slutdatum") }
                        Input(InputType.Date) {
                            value(endInput)
                            onInput { endInput = it.value }
                            if (!isManager && vac.userId != currentUserId) attr("readonly", "true")
                            style { absenceInputStyle() }
                        }
                    }

                    if (isManager) {
                        Div {
                            Span({ style { absenceLabelStyle() } }) { Text("Status") }
                            Select({
                                onInput { statusInput = it.value ?: "approved" }
                                style { absenceInputStyle() }
                            }) {
                                Option("approved", attrs = { if (statusInput == "approved") selected() }) { Text("Godkänd") }
                                Option("pending", attrs = { if (statusInput == "pending") selected() }) { Text("Väntar") }
                                Option("rejected", attrs = { if (statusInput == "rejected") selected() }) { Text("Nekad") }
                            }
                        }
                    } else {
                        Div {
                            Span({ style { absenceLabelStyle() } }) { Text("Status") }
                            val (badgeBg, badgeText, statusLabel) = when (vac.status) {
                                "approved" -> Triple("var(--rp-ok-bg)", "var(--rp-ok-text)", "Godkänd")
                                "rejected" -> Triple("var(--rp-err-bg)", "var(--rp-err-text)", "Nekad")
                                else -> Triple("var(--rp-warn-bg)", "var(--rp-warn-text)", "Väntar")
                            }
                            Div({
                                style {
                                    property("padding", "0.7rem 1rem"); property("background", badgeBg)
                                    property("color", badgeText); property("border-radius", "14px")
                                    property("font-weight", "700"); property("font-size", "1rem")
                                }
                            }) { Text(statusLabel) }
                        }
                    }

                    Div {
                        Span({ style { absenceLabelStyle() } }) { Text("Anteckning") }
                        TextArea {
                            value(noteInput)
                            onInput { noteInput = it.value }
                            if (!isManager && vac.userId != currentUserId) attr("readonly", "true")
                            style {
                                absenceInputStyle()
                                property("height", "80px")
                            }
                        }
                    }

                    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("margin-top", "1rem") } }) {
                        if (isManager || vac.userId == currentUserId) {
                            Button({
                                onClick {
                                    if (isDeleting) return@onClick
                                    isDeleting = true
                                    semesterScope.launch {
                                        absenceRepo.deleteAbsence(vac.id).onSuccess {
                                            feedbackMsg = "Semesteransökan raderad"
                                            editingAbsence = null
                                            loadData()
                                        }.onFailure {
                                            feedbackMsg = "Kunde inte radera semesteransökan"
                                        }
                                        isDeleting = false
                                    }
                                }
                                style { property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)"); property("border", "none"); property("padding", "0.6rem 1.2rem"); property("border-radius", "8px"); property("font-weight", "600"); property("cursor", "pointer") }
                            }) { Text(if (isDeleting) "Raderar..." else "Ta bort") }
                        } else {
                            Div {}
                        }

                        Div({ style { property("display", "flex"); property("gap", "0.5rem") } }) {
                            Button({
                                onClick { editingAbsence = null }
                                style { property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)"); property("border", "none"); property("padding", "0.6rem 1rem"); property("border-radius", "8px"); property("font-weight", "600"); property("cursor", "pointer") }
                            }) { Text("Stäng") }

                            if (isManager || vac.userId == currentUserId) {
                                Button({
                                    onClick {
                                        if (isSaving) return@onClick
                                        isSaving = true
                                        semesterScope.launch {
                                            absenceRepo.updateAbsence(vac.id, UpdateAbsenceRequest(
                                                type = vac.type,
                                                startDate = startInput,
                                                endDate = endInput,
                                                note = noteInput.ifBlank { null },
                                                status = if (isManager) statusInput else vac.status
                                            )).onSuccess {
                                                feedbackMsg = "Semesteransökan uppdaterad"
                                                editingAbsence = null
                                                loadData()
                                            }.onFailure {
                                                feedbackMsg = "Kunde inte uppdatera semesteransökan"
                                            }
                                            isSaving = false
                                        }
                                    }
                                    style { property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white"); property("border", "none"); property("padding", "0.6rem 1.2rem"); property("border-radius", "8px"); property("font-weight", "600"); property("cursor", "pointer") }
                                }) { Text(if (isSaving) "Sparar..." else "Spara") }
                            }
                        }
                    }
                }
            }
        }
    }

    // REQUIREMENT BUILDER MODAL
    if (showRequirementModal) {
        var reqName by remember { mutableStateOf("") }
        var reqStart by remember { mutableStateOf("") }
        var reqEnd by remember { mutableStateOf("") }
        val dayCountsMap = remember { mutableStateMapOf<Pair<Long, Int>, Int>() }

        LaunchedEffect(editingRequirementId) {
            val req = requirements.find { it.id == editingRequirementId }
            if (req != null) {
                reqName = req.name
                reqStart = req.startDate
                reqEnd = req.endDate
                dayCountsMap.clear()
                req.days.forEach { d ->
                    dayCountsMap[d.roleId to d.dayOfWeek] = d.count
                }
            } else {
                reqName = ""
                reqStart = ""
                reqEnd = ""
                dayCountsMap.clear()
            }
        }

        Div({
            onClick { showRequirementModal = false; editingRequirementId = null }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(13,13,24,0.45)"); property("backdrop-filter", "blur(4px)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem")
                    property("width", "100%"); property("max-width", "640px"); property("max-height", "90vh"); property("overflow-y", "auto")
                    property("box-shadow", "0 24px 80px rgba(13,13,24,0.18)")
                }
            }) {
                H3({ style { property("margin", "0 0 1rem 0"); property("font-weight", "800") } }) { Text("Planeringskrav") }

                // List existing requirements
                if (requirements.isNotEmpty()) {
                    Div({ style { property("margin-bottom", "1.5rem"); property("border-bottom", "1px solid var(--rp-hl-med)"); property("padding-bottom", "1rem") } }) {
                        Span({ style { absenceLabelStyle() } }) { Text("Aktiva planeringskrav") }
                        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.5rem") } }) {
                            requirements.forEach { r ->
                                Div({ style {
                                    property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                                    property("padding", "0.6rem 0.8rem"); property("background", "var(--rp-overlay)"); property("border-radius", "12px")
                                } }) {
                                    Div {
                                        Div({ style { property("font-weight", "700"); property("font-size", "0.88rem") } }) { Text(r.name) }
                                        Div({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)") } }) { Text("${r.startDate} till ${r.endDate}") }
                                    }
                                    Div({ style { property("display", "flex"); property("gap", "0.5rem") } }) {
                                        Button({
                                            onClick {
                                                editingRequirementId = r.id
                                            }
                                            style { property("background", "none"); property("border", "none"); property("color", "var(--rp-pine)"); property("cursor", "pointer") }
                                        }) { FaIcon("fa-solid fa-pen", size = "0.85rem") }

                                        Button({
                                            onClick {
                                                semesterScope.launch {
                                                    requirementRepo.deleteRequirement(workplaceId, r.id)
                                                    if (editingRequirementId == r.id) {
                                                        editingRequirementId = null
                                                    }
                                                    loadData()
                                                }
                                            }
                                            style { property("background", "none"); property("border", "none"); property("color", "var(--rp-love)"); property("cursor", "pointer") }
                                        }) { FaIcon("fa-solid fa-trash-can", size = "0.85rem") }
                                    }
                                }
                            }
                        }
                    }
                }

                // Create/Edit form
                Span({ style { absenceLabelStyle() } }) { Text(if (editingRequirementId != null) "Redigera krav" else "Skapa nytt krav") }

                if (editingRequirementId != null) {
                    Button({
                        onClick { editingRequirementId = null }
                        style { property("margin-bottom", "1rem"); property("padding", "0.3rem 0.6rem"); property("font-size", "0.75rem"); property("background", "var(--rp-hl-med)"); property("border", "none"); property("border-radius", "6px"); property("cursor", "pointer") }
                    }) { Text("Växla till skapa ny") }
                }
                
                Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "1rem"); property("margin-bottom", "1rem") } }) {
                    Div {
                        Label(attrs = { style { absenceLabelStyle() } }) { Text("Namn") }
                        Input(InputType.Text) {
                            value(reqName)
                            onInput { reqName = it.value }
                            attr("placeholder", "t.ex. Sommar, Helgbeläggning")
                            style { absenceInputStyle() }
                        }
                    }
                    Div {}
                    Div {
                        Label(attrs = { style { absenceLabelStyle() } }) { Text("Startdatum") }
                        Input(InputType.Date) {
                            value(reqStart)
                            onInput { reqStart = it.value }
                            style { absenceInputStyle() }
                        }
                    }
                    Div {
                        Label(attrs = { style { absenceLabelStyle() } }) { Text("Slutdatum") }
                        Input(InputType.Date) {
                            value(reqEnd)
                            onInput { reqEnd = it.value }
                            style { absenceInputStyle() }
                        }
                    }
                }

                if (allRoles.isEmpty()) {
                    P({ style { property("color", "var(--rp-muted)"); property("font-size", "0.85rem"); property("margin-bottom", "1.5rem") } }) {
                        Text("Du måste definiera custom roller under 'Hantera roller' i Personalfliken innan du kan sätta krav.")
                    }
                } else {
                    Div({ style { property("margin-bottom", "1.5rem") } }) {
                        Label(attrs = { style { absenceLabelStyle() } }) { Text("Krav per dag och roll") }
                        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
                            allRoles.forEach { role ->
                                Div({ style { property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "14px"); property("padding", "0.8rem") } }) {
                                    Div({ style { property("font-weight", "700"); property("font-size", "0.85rem"); property("margin-bottom", "0.5rem"); property("color", "var(--rp-pine)") } }) {
                                        Text(role.name)
                                    }
                                    Div({ style { property("display", "flex"); property("gap", "0.4rem"); property("overflow-x", "auto") } }) {
                                        listOf("Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön").forEachIndexed { idx, dayLabel ->
                                            val dayOfWeek = idx + 1
                                            val currentVal = dayCountsMap[role.id to dayOfWeek] ?: 0
                                            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("align-items", "center"); property("gap", "0.2rem"); property("min-width", "50px") } }) {
                                                Span({ style { property("font-size", "0.68rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) { Text(dayLabel) }
                                                Input(InputType.Number) {
                                                    value(currentVal.toString())
                                                    onInput {
                                                        val v = it.value?.toInt() ?: 0
                                                        dayCountsMap[role.id to dayOfWeek] = v
                                                    }
                                                    style {
                                                        property("width", "40px"); property("padding", "0.3rem"); property("text-align", "center")
                                                        property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "8px"); property("font-size", "0.85rem")
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "0.75rem") } }) {
                    Button({
                        onClick { showRequirementModal = false; editingRequirementId = null }
                        style { property("padding", "0.6rem 1.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text("Avbryt") }
                    Button({
                        onClick {
                            if (reqName.isNotBlank() && reqStart.isNotBlank() && reqEnd.isNotBlank()) {
                                val dayCounts = dayCountsMap.map { (key, count) ->
                                    RequirementDayCountDto(
                                        dayOfWeek = key.second,
                                        roleId = key.first,
                                        count = count
                                    )
                                }
                                semesterScope.launch {
                                    val editId = editingRequirementId
                                    if (editId != null) {
                                        requirementRepo.updateRequirement(
                                            workplaceId,
                                            editId,
                                            CreateWorkplaceRequirementRequest(
                                                name = reqName,
                                                startDate = reqStart,
                                                endDate = reqEnd,
                                                days = dayCounts
                                            )
                                        )
                                        editingRequirementId = null
                                    } else {
                                        requirementRepo.createRequirement(
                                            workplaceId,
                                            CreateWorkplaceRequirementRequest(
                                                name = reqName,
                                                startDate = reqStart,
                                                endDate = reqEnd,
                                                days = dayCounts
                                            )
                                        )
                                    }
                                    loadData()
                                    showRequirementModal = false
                                }
                            }
                        }
                        style {
                            property("padding", "0.6rem 1.5rem"); property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                            property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer")
                        }
                    }) { Text(if (editingRequirementId != null) "Spara ändringar" else "Spara krav") }
                }
            }
        }
    }
}
}

private fun org.jetbrains.compose.web.css.StyleScope.absenceLabelStyle() {
    property("display", "block"); property("font-weight", "700"); property("font-size", "0.75rem"); property("color", "var(--rp-subtle)")
    property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.absenceInputStyle() {
    property("width", "100%"); property("padding", "0.7rem 1rem"); property("border", "2px solid var(--rp-hl-med)")
    property("border-radius", "14px"); property("font-size", "1rem"); property("font-weight", "600")
    property("outline", "none"); property("box-sizing", "border-box")
}

private fun todayLocalDate(): String {
    val date = kotlin.js.Date()
    val y = date.getFullYear()
    val m = (date.getMonth() + 1).toString().padStart(2, '0')
    val d = date.getDate().toString().padStart(2, '0')
    return "$y-$m-$d"
}
